// Écran 2 — Carte : graphe interactif des objets et des liens (filtres par type, zone, criticité), fiche d'un objet
// avec sa provenance (source, extrait cité, confiance) et ses contradictions, et les six vues du guide de
// cartographie de l'ANSSI (lues dans l'export de l'API).
import { useEffect, useMemo, useRef, useState } from 'react';
import { EtatVide } from '../composants/EtatVide';
import { importMarque } from '../domaine/importation';
import type { Contradiction, Lien, Objet, ObjetConstat, TypeObjet, VueAnssi } from '../api/types';
import { Attendre, BlocProvenance, Citations, Erreur, Etiquette, EtiquetteGravite } from '../composants/communs';
import { useCarte, useConstats, useContradictions, useEkera, useVueAnssi } from '../contexte';
import {
  COUCHE_DU_TYPE,
  contradictionsDe,
  disposer,
  filtrer,
  idsDeVue,
  carteVide,
  indexer,
  nomCourt,
  nomDe,
  objetsParLigne,
  ORDRE_COUCHES,
  TYPES_PAR_DEFAUT,
  type Couche,
  type IndexCarte,
} from '../domaine/carte';
import { lireCsv } from '../domaine/csv';
import { fourchette, LIBELLE_LIEN, LIBELLE_TYPE, valeurLisible, VUES_ANSSI } from '../domaine/format';
import { telecharger } from '../domaine/telechargement';
import { lienVers, naviguer } from '../routeur';

const COULEURS: Record<Couche, [string, string]> = {
  metier: ['var(--biz)', 'var(--biz-line)'],
  applicatif: ['var(--app)', 'var(--app-line)'],
  technique: ['var(--tech)', 'var(--tech-line)'],
  reseau: ['var(--net)', 'var(--net-line)'],
  ecosysteme: ['var(--eco)', 'var(--eco-line)'],
};

const CRITICITES = [
  { v: 0, l: 'Toutes' },
  { v: 2, l: '2 et plus' },
  { v: 3, l: '3 et plus' },
  { v: 4, l: '4 (vital)' },
];

export function EcranCarte({ params }: { params: URLSearchParams }) {
  const carte = useCarte();
  const constats = useConstats();
  const contradictions = useContradictions();
  if (carte.data && carteVide(carte.data))
    return (
      <EtatVide
        titre="Votre carte est vide pour l’instant"
        idTitre="t-carte-vide"
        texte={[
          'La carte dessine votre système d’information : applications, machines, comptes, fournisseurs, sauvegardes et les liens entre eux. Elle se remplit à la première lecture des pièces que vous déposez ; chaque élément cite la pièce et le passage d’où il vient.',
          'Pour commencer : un inventaire (GLPI), l’annuaire et la configuration du pare-feu suffisent à faire apparaître les premiers éléments.',
        ]}
      />
    );
  return (
    <Attendre etats={[carte, constats, contradictions]} quoi="la carte">
      {() => (
        <VueCarte
          ix={indexer(carte.data!)}
          constats={constats.data!.constats}
          liensConstats={constats.data!.liens}
          contradictions={contradictions.data!}
          params={params}
        />
      )}
    </Attendre>
  );
}

function VueCarte({
  ix,
  constats,
  liensConstats,
  contradictions,
  params,
}: {
  ix: IndexCarte;
  constats: ObjetConstat[];
  liensConstats: Lien[];
  contradictions: Contradiction[];
  params: URLSearchParams;
}) {
  const choisi = params.get('objet');
  const vue = (params.get('vue') as VueAnssi | null) ?? null;
  const [types, setTypes] = useState<Set<TypeObjet>>(() => new Set(TYPES_PAR_DEFAUT));
  const [zone, setZone] = useState<string>('');
  const [critMin, setCritMin] = useState(0);
  const [recherche, setRecherche] = useState('');
  const [affichage, setAffichage] = useState<'graphe' | 'liste'>('graphe');
  const csv = useVueAnssi(vue);
  const lignesVue = useMemo(() => (csv.data ? lireCsv(csv.data) : null), [csv.data]);
  const idsVue = useMemo(() => (lignesVue ? idsDeVue(lignesVue) : null), [lignesVue]);

  const typesPresents = useMemo(() => [...new Set(ix.objets.map((o) => o.type))], [ix]);
  const zones = ix.objets.filter((o) => o.type === 'zone_reseau');
  const visibles = filtrer(ix, { types, zone: zone || null, criticiteMin: critMin, idsVue: vue ? idsVue ?? new Set() : null, recherche });
  const constatsPar = useMemo(() => {
    const m = new Map<string, ObjetConstat[]>();
    const parId = new Map(constats.map((c) => [c.id, c]));
    for (const l of liensConstats) {
      const c = parId.get(l.source);
      if (l.type === 'concerne' && c) m.set(l.cible, [...(m.get(l.cible) ?? []), c]);
    }
    return m;
  }, [constats, liensConstats]);

  const choisir = (id: string | null) =>
    naviguer('carte', { objet: id ?? undefined, vue: vue ?? undefined });
  const objetChoisi = choisi ? ix.parId.get(choisi) ?? null : null;

  return (
    <div className="pile">
      <p className="sous-titre">
        Construite en croisant vos sources et l’entretien. Choisissez un élément pour voir d’où vient chaque
        information, avec quelle confiance, et ce qui se contredit.
      </p>
      <div className="onglets" role="group" aria-label="Vues du guide de cartographie de l’ANSSI">
        <button type="button" aria-pressed={!vue} onClick={() => naviguer('carte', { objet: choisi ?? undefined })}>
          Toutes les couches
        </button>
        {VUES_ANSSI.map((v) => (
          <button
            key={v.id}
            type="button"
            aria-pressed={vue === v.id}
            title={v.description}
            onClick={() => naviguer('carte', { vue: v.id, objet: choisi ?? undefined })}
          >
            {v.libelle}
          </button>
        ))}
      </div>
      {vue && (
        <p className="note">
          Vue « {VUES_ANSSI.find((v) => v.id === vue)?.libelle} » du guide de cartographie de l’ANSSI (2018) :{' '}
          {VUES_ANSSI.find((v) => v.id === vue)?.description.toLowerCase()}. Contenu lu dans l’export de l’API ; les
          colonnes sont un choix d’Ekera, à relire avec le guide.
        </p>
      )}

      <div className="panneau">
        <div className="filtres">
          {!vue && (
            <fieldset className="types">
              <legend>Types d’objets</legend>
              {typesPresents.map((t) => (
                <label key={t} className="case">
                  <input
                    type="checkbox"
                    checked={types.has(t)}
                    onChange={(e) => {
                      const n = new Set(types);
                      if (e.target.checked) n.add(t);
                      else n.delete(t);
                      setTypes(n);
                    }}
                  />
                  {LIBELLE_TYPE[t]}
                </label>
              ))}
            </fieldset>
          )}
          <div className="champ">
            <label htmlFor="f-zone">Zone réseau</label>
            <select id="f-zone" value={zone} onChange={(e) => setZone(e.target.value)}>
              <option value="">Toutes</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="f-crit">Criticité</label>
            <select id="f-crit" value={critMin} onChange={(e) => setCritMin(Number(e.target.value))}>
              {CRITICITES.map((c) => (
                <option key={c.v} value={c.v}>
                  {c.l}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="f-cherche">Chercher</label>
            <input id="f-cherche" type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="ERP, VPN…" />
          </div>
          <div className="onglets" role="group" aria-label="Affichage">
            <button type="button" aria-pressed={affichage === 'graphe'} onClick={() => setAffichage('graphe')}>
              Graphe
            </button>
            <button type="button" aria-pressed={affichage === 'liste'} onClick={() => setAffichage('liste')}>
              Liste
            </button>
          </div>
        </div>
        <p className="note" role="status">
          {visibles.length} objet{visibles.length > 1 ? 's' : ''} affiché{visibles.length > 1 ? 's' : ''} sur {ix.objets.length}
          {vue && csv.isPending ? ' (lecture de la vue…)' : ''}
        </p>
      </div>
      {csv.error ? <Erreur erreur={csv.error} titre="Vue de l’ANSSI illisible." /> : null}

      <div className="carte-zone">
        <div className="pile">
          {affichage === 'graphe' ? (
            <Graphe ix={ix} visibles={visibles} choisi={choisi} surChoix={choisir} constatsPar={constatsPar} />
          ) : (
            <ListeObjets ix={ix} visibles={visibles} choisi={choisi} surChoix={choisir} />
          )}
          <Legende />
        </div>
        <aside className="panneau fiche" aria-label="Fiche de l’objet" aria-live="polite">
          {objetChoisi ? (
            <Fiche
              ix={ix}
              o={objetChoisi}
              constats={constatsPar.get(objetChoisi.id) ?? []}
              contradictions={contradictionsDe(contradictions, objetChoisi.id)}
              surChoix={choisir}
            />
          ) : (
            <>
              <h2>Choisissez un élément</h2>
              <p className="muted">Chaque fiche indique ses sources, la confiance d’Ekera et les écarts trouvés.</p>
              {contradictions.filter((c) => c.etat !== 'resolue').length > 0 && (
                <p className="encadre attention">
                  {contradictions.filter((c) => c.etat !== 'resolue').length} contradiction(s) ouverte(s) entre vos sources.
                </p>
              )}
            </>
          )}
        </aside>
      </div>
      {vue && lignesVue && <TableVue vue={vue} lignes={lignesVue} texte={csv.data ?? ''} surChoix={choisir} />}
    </div>
  );
}

function Legende() {
  return (
    <div className="legende" aria-label="Légende">
      {ORDRE_COUCHES.map((c) => (
        <span key={c.id}>
          <i style={{ background: COULEURS[c.id][0], borderColor: COULEURS[c.id][1] }} />
          {c.libelle}
        </span>
      ))}
      <span>
        <svg width="26" height="10" aria-hidden="true">
          <line x1="0" y1="5" x2="26" y2="5" stroke="var(--rust)" strokeWidth="2" strokeDasharray="5 3" />
        </svg>{' '}
        passage réseau ouvert
      </span>
      <span>« ! » : constat ouvert</span>
    </div>
  );
}

function Graphe({
  ix,
  visibles,
  choisi,
  surChoix,
  constatsPar,
}: {
  ix: IndexCarte;
  visibles: Objet[];
  choisi: string | null;
  surChoix: (id: string | null) => void;
  constatsPar: Map<string, ObjetConstat[]>;
}) {
  const conteneur = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(1100);
  useEffect(() => {
    const el = conteneur.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => e && setLargeur(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const d = useMemo(() => disposer(ix, visibles, objetsParLigne(largeur)), [ix, visibles, largeur]);
  useEffect(() => {
    // L'objet choisi (depuis un lien ou la liste) est amené dans la partie visible du graphe.
    if (!choisi) return;
    const c = conteneur.current;
    const n = d.noeuds.find((x) => x.id === choisi);
    if (!c || !n) return;
    // Défilement du seul conteneur du graphe (la page ne bouge pas).
    if (n.y < c.scrollTop || n.y + n.h > c.scrollTop + c.clientHeight) c.scrollTop = Math.max(0, n.y - 60);
    if (n.x < c.scrollLeft || n.x + n.l > c.scrollLeft + c.clientWidth) c.scrollLeft = Math.max(0, n.x - 40);
  }, [choisi, d]);
  const pos = new Map(d.noeuds.map((n) => [n.id, n]));
  const voisins = new Set<string>();
  if (choisi) {
    voisins.add(choisi);
    for (const l of d.liens) {
      if (l.source === choisi) voisins.add(l.cible);
      if (l.cible === choisi) voisins.add(l.source);
    }
  }
  return (
    <div className="graphe" ref={conteneur}>
      {!d.noeuds.length && <p className="encadre info">Aucun objet ne correspond aux filtres.</p>}
      <svg
        width={d.largeur}
        height={d.hauteur}
        viewBox={`0 0 ${d.largeur} ${d.hauteur}`}
        role="group"
        aria-label={`Graphe de ${d.noeuds.length} objets et ${d.liens.length} liens`}
      >
        <defs>
          <marker id="fleche" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill="var(--ink-2)" />
          </marker>
        </defs>
        {d.couches.map((c) => (
          <text key={c.id} x={16} y={c.y - 12} fontSize="11.5" fontWeight="600" letterSpacing="1" fill="var(--ink-2)">
            {c.libelle.toUpperCase()}
          </text>
        ))}
        <g aria-hidden="true">
          {d.liens.map((l) => {
            const a = pos.get(l.source);
            const b = pos.get(l.cible);
            if (!a || !b) return null;
            const ouvert = l.type === 'passage_reseau' && (l.attributs as { ouvert?: boolean } | undefined)?.ouvert;
            const actif = choisi && (l.source === choisi || l.cible === choisi);
            const memeLigne = a.y === b.y;
            const x1 = a.x + a.l / 2;
            const x2 = b.x + b.l / 2;
            const y1 = memeLigne ? a.y : b.y > a.y ? a.y + a.h : a.y;
            const y2 = memeLigne ? b.y : b.y > a.y ? b.y : b.y + b.h;
            const dy = memeLigne ? -40 : (y2 - y1) / 2;
            const chemin = memeLigne
              ? `M${x1} ${y1} C${x1} ${y1 + dy} ${x2} ${y2 + dy} ${x2} ${y2}`
              : `M${x1} ${y1} C${x1} ${y1 + dy} ${x2} ${y2 - dy} ${x2} ${y2}`;
            return (
              <path
                key={l.id}
                d={chemin}
                fill="none"
                stroke={ouvert ? 'var(--rust)' : actif ? 'var(--ink)' : 'var(--part)'}
                strokeWidth={actif || ouvert ? 2 : 1.2}
                strokeDasharray={ouvert ? '6 4' : undefined}
                markerEnd={l.type === 'passage_reseau' || l.type === 'flux' ? 'url(#fleche)' : undefined}
                className={choisi && !actif ? 'estompe' : undefined}
              />
            );
          })}
        </g>
        {d.noeuds.map((n) => {
          const o = ix.parId.get(n.id)!;
          const [fond, trait] = COULEURS[COUCHE_DU_TYPE[o.type]];
          const nb = constatsPar.get(n.id)?.length ?? 0;
          const libelle = nomCourt(o);
          const court = libelle.length > 18 ? `${libelle.slice(0, 17)}…` : libelle;
          const actif = n.id === choisi;
          return (
            <g
              key={n.id}
              className={`noeud${actif ? ' choisi' : ''}${choisi && !voisins.has(n.id) ? ' estompe' : ''}`}
              tabIndex={0}
              role="button"
              aria-pressed={actif}
              aria-label={`${LIBELLE_TYPE[o.type]} : ${o.nom}${nb ? `, ${nb} constat${nb > 1 ? 's' : ''}` : ''}`}
              data-objet={n.id}
              onClick={() => surChoix(actif ? null : n.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  surChoix(actif ? null : n.id);
                }
              }}
            >
              <title>{o.nom}</title>
              <rect x={n.x} y={n.y} width={n.l} height={n.h} rx={o.type === 'processus' ? 18 : 6} fill={fond} stroke={trait} strokeWidth={1.5} />
              <text x={n.x + n.l / 2} y={n.y + 25} textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--ink)">
                {court}
              </text>
              {nb > 0 && (
                <g aria-hidden="true">
                  <circle cx={n.x + n.l - 4} cy={n.y + 4} r={9} fill="var(--crit)" />
                  <text x={n.x + n.l - 4} y={n.y + 8} textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">
                    !
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ListeObjets({
  ix,
  visibles,
  choisi,
  surChoix,
}: {
  ix: IndexCarte;
  visibles: Objet[];
  choisi: string | null;
  surChoix: (id: string | null) => void;
}) {
  return (
    <div className="panneau tableau-defile">
      <table>
        <caption>Objets affichés</caption>
        <thead>
          <tr>
            <th scope="col">Nom</th>
            <th scope="col">Type</th>
            <th scope="col">Zone</th>
            <th scope="col" className="num">
              Criticité
            </th>
            <th scope="col">Statut</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((o) => (
            <tr key={o.id} data-choisi={o.id === choisi}>
              <td>
                <button type="button" className="lien-btn" onClick={() => surChoix(o.id)}>
                  {o.nom}
                </button>
              </td>
              <td>{LIBELLE_TYPE[o.type]}</td>
              <td>{ix.zone.get(o.id) ? nomDe(ix, ix.zone.get(o.id)!) : '—'}</td>
              <td className="num">{ix.criticite.get(o.id) ?? '—'}</td>
              <td>{o.provenance?.statut ?? 'propose'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Fiche({
  ix,
  o,
  constats,
  contradictions,
  surChoix,
}: {
  ix: IndexCarte;
  o: Objet;
  constats: ObjetConstat[];
  contradictions: Contradiction[];
  surChoix: (id: string | null) => void;
}) {
  const { ecritureAutorisee } = useEkera();
  const attributs = Object.entries((o.attributs ?? {}) as Record<string, unknown>);
  const pa = (o.provenance_attributs ?? {}) as Record<string, NonNullable<Objet['provenance']>>;
  const sortants = ix.sortants.get(o.id) ?? [];
  const entrants = ix.entrants.get(o.id) ?? [];
  return (
    <div className="pile" style={{ gap: 12 }}>
      <div>
        <h2>{o.nom}</h2>
        <p className="note">
          {LIBELLE_TYPE[o.type]} · version {o.version ?? 1}
        </p>
        <details className="note">
          <summary>Détails techniques</summary>
          Identifiant : <span className="num">{o.id}</span>
        </details>
      </div>
      <dl>
        {ix.criticite.has(o.id) && (
          <>
            <dt>Criticité</dt>
            <dd>{ix.criticite.get(o.id)} sur 4</dd>
          </>
        )}
        {ix.zone.has(o.id) && (
          <>
            <dt>Zone</dt>
            <dd>{nomDe(ix, ix.zone.get(o.id)!)}</dd>
          </>
        )}
        {attributs.map(([k, v]) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{k.replace(/_/g, ' ')}</dt>
            <dd>
              {valeurLisible(v)}
              {pa[k] && (
                <>
                  {' '}
                  <Etiquette ton="info">
                    {importMarque(pa[k]) ? 'validation importée' : `corrigé par ${pa[k].valide_par ?? 'le DSI'}`}
                  </Etiquette>
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {ecritureAutorisee && (
        <div className="ligne">
          <a className="btn petit principal" href={lienVers('corrections', { element: o.id })}>
            Corriger ou valider
          </a>
        </div>
      )}
      <section className="pile" style={{ gap: 6 }} aria-label="Provenance">
        <h3>Provenance</h3>
        <BlocProvenance p={o.provenance} />
      </section>
      {Object.keys(pa).length > 0 && (
        <details>
          <summary>Provenance par attribut corrigé ({Object.keys(pa).length})</summary>
          {Object.entries(pa).map(([k, p]) => (
            <div key={k} style={{ marginTop: 8 }}>
              <BlocProvenance p={p} titre={k.replace(/_/g, ' ')} />
            </div>
          ))}
        </details>
      )}
      <section className="pile" style={{ gap: 6 }} aria-label="Points à confirmer">
        <h3>Points à confirmer avant de prendre une décision</h3>
        {contradictions.length === 0 ? (
          <p className="note">Aucun point à confirmer : les sources ne se contredisent pas sur cet objet.</p>
        ) : (
          contradictions.map((c) => (
            <div key={c.id} className="encadre attention">
              <p>
                <b>{c.champ ?? c.sujet}</b> : {(c.valeurs ?? []).map((v) => valeurLisible(v)).join(' ou ')}{' '}
                <Etiquette ton={c.etat === 'resolue' ? 'ok' : 'attention'}>{c.etat === 'resolue' ? 'Résolue' : 'Ouverte'}</Etiquette>
              </p>
              {c.resolution && <p className="note">Résolution : {c.resolution}</p>}
              <Citations citations={c.citations ?? []} />
            </div>
          ))
        )}
      </section>
      {constats.length > 0 && (
        <section className="pile" style={{ gap: 6 }} aria-label="Constats">
          <h3>Constats</h3>
          {constats.some((c) => c.attributs?.perte_estimee) && (
            <p className="note" data-testid="note-pertes-constats">
              Les euros sont des pertes potentielles estimées si le constat est exploité, conditionnelles aux hypothèses
              retenues (calibration provisoire, non validée par un actuaire).
            </p>
          )}
          <ul className="liste-points">
            {constats.map((c) => (
              <li key={c.id}>
                <a href={lienVers('constats', { constat: c.id })}>{c.nom}</a>
                <small className="ligne">
                  <EtiquetteGravite g={c.attributs?.gravite} />
                  {c.attributs?.perte_estimee && fourchette(c.attributs.perte_estimee.bas.valeur, c.attributs.perte_estimee.haut.valeur)}
                </small>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="pile" style={{ gap: 6 }} aria-label="Liens">
        <h3>Liens ({sortants.length + entrants.length})</h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
          {sortants.map((l) => (
            <li key={l.id}>
              {LIBELLE_LIEN[l.type]}{' '}
              <button type="button" className="lien-btn" onClick={() => surChoix(l.cible)}>
                {nomDe(ix, l.cible)}
              </button>
            </li>
          ))}
          {entrants.map((l) => (
            <li key={l.id}>
              <button type="button" className="lien-btn" onClick={() => surChoix(l.source)}>
                {nomDe(ix, l.source)}
              </button>{' '}
              {LIBELLE_LIEN[l.type]} cet objet
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function TableVue({
  vue,
  lignes,
  texte,
  surChoix,
}: {
  vue: VueAnssi;
  lignes: string[][];
  texte: string;
  surChoix: (id: string | null) => void;
}) {
  const [entete = [], ...corps] = lignes;
  const colId = entete.indexOf('id');
  const libelle = VUES_ANSSI.find((v) => v.id === vue)?.libelle ?? vue;
  return (
    <section className="panneau" aria-labelledby="t-vue">
      <div className="panneau-tete">
        <h2 id="t-vue">Vue {libelle} : tableau</h2>
        <button
          type="button"
          className="btn petit"
          onClick={() => telecharger(new Blob([texte], { type: 'text/csv;charset=utf-8' }), `anssi-${vue}.csv`)}
        >
          Télécharger le CSV
        </button>
      </div>
      <div className="tableau-defile">
        <table>
          <caption className="sr">Vue {libelle} du guide de cartographie de l’ANSSI</caption>
          <thead>
            <tr>
              {entete.map((h) => (
                <th key={h} scope="col">
                  {h.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {corps.map((l, i) => (
              <tr key={i}>
                {l.map((v, j) => (
                  <td key={j}>
                    {j === colId ? (
                      <button type="button" className="lien-btn num" onClick={() => surChoix(v)}>
                        {v}
                      </button>
                    ) : (
                      v
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
